import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Star,
  Phone,
  MapPin,
  Instagram,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import {
  SERVICES,
  SPA_SERVICES,
  LOCATIONS,
  TESTIMONIALS,
  IMAGES,
  HERO_IMAGES,
  BRAND,
} from "../data/site";
import { SectionHeading } from "../components/SectionHeading";
import { Seo } from "../components/Seo";
import {
  SEO,
  organizationSchema,
  websiteSchema,
  dentistSchema,
  aggregateRatingSchema,
} from "../data/seo";
import { SpaServicesSection } from "../components/SpaServicesSection";
import { VideoTestimonialsSection } from "../components/VideoTestimonialsSection";
import { useHeroContext } from "../lib/HeroContext";
import { SmileGalleryGrid } from "../components/SmileGalleryGrid";

const HERO_GRAY_SWATCHES = [
  "#bdbdbd",
  "#939393",
  "#7e7e7e",
  "#696969",
  "#6B6F77",
  "#544f4a",
];

const HeroPane = ({
  title,
  smallTitle,
  smallTitleColor,
  subtitle,
  location,
  link,
  ctaButtons = [],
  testId,
  hoveredPane,
  setHoveredPane,
  paneKey,
  side = "side",
  heroColor,
  setHeroColor,
}) => {
  const isActive = hoveredPane === paneKey;
  // const isActive = true;
  const anyActive = hoveredPane !== null;
  const dimmed = anyActive && !isActive;
  const isCenter = side === "center";
  const smallTitleClass =
    {
      "emeraled-green": "text-[#00ff1a]",
      "emerald-green": "text-[#00ff1a]",
      blue: "text-[#00FFFF]",
      gold: "text-gold-light",
      cream: "text-cream",
    }[smallTitleColor] || "text-gold-light";

  return (
    <Link
      to={link || "#"}
      data-testid={testId}
      onMouseEnter={() => setHoveredPane(paneKey)}
      onMouseLeave={() => setHoveredPane(null)}
      onFocus={() => setHoveredPane(paneKey)}
      onBlur={() => setHoveredPane(null)}
      className="group relative h-[55vh] md:h-screen md:min-h-[680px] overflow-hidden block transition-all duration-700"
    >
      <div
        className={`absolute inset-0 transition-all duration-700 pointer-events-none ${
          isActive ? "bg-ink/5" : dimmed ? "bg-ink/40" : "bg-ink/15"
        }`}
      />
      <div className="absolute top-0 bottom-0 left-0 w-px bg-cream/15 pointer-events-none hidden md:block" />

      {isCenter ? (
        // CENTER pane — brand at TOP so model's face stays visible
        <div className="relative h-full flex flex-col items-center py-12 text-center">
          <div className="mt-10 mr-10 flex items-center justify-center gap-1.5">
            {HERO_GRAY_SWATCHES.map((color) => (
              <button
                key={color}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setHeroColor(color);
                }}
                className={`w-5 h-3 border hover:scale-105 transition-transform ${
                  heroColor === color
                    ? "border-gold ring-1 ring-gold"
                    : "border-white/40"
                }`}
                style={{ backgroundColor: color }}
                title={color}
                aria-label={`Hero color ${color}`}
                aria-pressed={heroColor === color}
              />
            ))}
          </div>
          <h1
            className={`font-script text-white group-hover:text-[#FDBE02] text-3xl md:text-4xl lg:text-5xl leading-none mt-4 drop-shadow-[0_4px_22px_rgba(0,0,0,0.55)] transition-all duration-700 px-6 md:px-10 ${
              isActive ? "scale-[1.03] " : ""
            }`}
          >
            {title}
            {smallTitle && (
              <span
                className={`ml-2 align-middle font-script text-base md:text-lg lg:text-xl tracking-wide ${smallTitleClass}`}
              >
                {smallTitle}
              </span>
            )}
          </h1>

          {/* Subtitle + CTA moved to top */}
          <div className="mt-2 flex flex-col items-center px-6 md:px-10">
            <div
              className={`overflow-hidden transition-all duration-700 ease-out max-w-md ${
                isActive ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <p
                className="
    font-serif italic text-white text-lg md:text-xl leading-snug
    truncate whitespace-nowrap
    relative
  "
                title={subtitle} // native browser tooltip
              >
                {subtitle}
              </p>
            </div>
            <div
              className={`overflow-hidden transition-all duration-700 grid grid-cols-3 gap-4 p-1 w-full mt-2 ${
                isActive ? "max-h-32 opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              {ctaButtons.map((btn, idx) => (
                <Link
                  key={idx}
                  to={btn.href}
                  onClick={(e) => e.stopPropagation()}
                  className="text-white text-[10px] tracking-[0.3em] uppercase border-t border-gold pt-1 hover:text-gold-light transition-colors text-center"
                >
                  {btn.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      ) : (
        // SIDE panes — script-font title vertically centered
        <div className="relative h-full flex flex-col items-center justify-center text-center px-6 md:px-8 py-12">
          <h2
            className={`font-script text-white text-3xl group-hover:text-[#FDBE02] md:text-4xl lg:text-5xl leading-none drop-shadow-[0_4px_18px_rgba(0,0,0,0.55)] transition-all duration-700 ${
              isActive ? "scale-[1.04] " : ""
            }`}
          >
            {title}
            {smallTitle && (
              <span
                className={`ml-2 align-middle font-script text-sm md:text-base lg:text-lg tracking-wide ${smallTitleClass}`}
              >
                {smallTitle}
              </span>
            )}
          </h2>
          {location && (
            <div className="text-[10px] tracking-[0.3em] uppercase text-cream mt-2 ">
              {location}
            </div>
          )}

          <div
            className={`overflow-hidden transition-all duration-700 ease-out max-w-xs ${
              isActive ? "max-h-40 opacity-100 mt-6" : "max-h-0 opacity-0 mt-0"
            }`}
          >
            <p className="font-serif italic text-cream/95 text-base md:text-lg leading-snug ]">
              {subtitle}
            </p>
          </div>

          <div
            className={`overflow-hidden transition-all duration-700 grid grid-cols-3 gap-4 w-full ${
              isActive ? "max-h-32 opacity-100 mt-2" : "max-h-0 opacity-0 mt-0"
            }`}
          >
            {ctaButtons.slice(0, 3).map((btn, idx) => (
              <Link
                key={idx}
                to={btn.href}
                onClick={(e) => e.stopPropagation()}
                className="text-white text-[10px] tracking-[0.3em] uppercase border-t border-gold pt-1 hover:text-gold-light transition-colors text-center"
              >
                {btn.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </Link>
  );
};

const TestimonialsCarouselEmbla = () => {
  const autoplay = useRef(Autoplay({ delay: 5000, stopOnInteraction: false }));
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start", slidesToScroll: 1 },
    [autoplay.current],
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return (
    <div className="mt-16 relative" data-testid="testimonials-carousel">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div
              key={i}
              className="flex-none w-full md:w-[calc(33.333%-16px)] bg-white/10 border border-white/15 p-8 flex flex-col"
            >
              <div className="flex gap-1 mb-5">
                {[...Array(5)].map((_, j) => (
                  <Star
                    key={j}
                    size={14}
                    className="fill-gold text-gold"
                    strokeWidth={0}
                  />
                ))}
              </div>
              <blockquote className="font-serif text-lg md:text-xl text-white font-light leading-snug flex-1">
                "{t.text}"
              </blockquote>
              <div className="mt-6 pt-6 border-t border-white/15">
                <div className="text-sm tracking-widest uppercase text-white">
                  {t.name}
                </div>
                <div className="text-xs tracking-widest uppercase text-white/50 mt-1">
                  {t.location}
                </div>
                {t.date && (
                  <div className="text-xs tracking-widest uppercase text-white/40 mt-1">
                    {t.date}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-center gap-4 mt-10">
        <button
          onClick={scrollPrev}
          className="w-11 h-11 border border-white/20 flex items-center justify-center text-white hover:border-gold hover:text-gold transition-colors"
          aria-label="Previous reviews"
        >
          <ChevronLeft size={18} strokeWidth={1.5} />
        </button>
        <span className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-mono">
          {selectedIndex + 1} / {TESTIMONIALS.length}
        </span>
        <button
          onClick={scrollNext}
          className="w-11 h-11 border border-white/20 flex items-center justify-center text-white hover:border-gold hover:text-gold transition-colors"
          aria-label="Next reviews"
        >
          <ChevronRight size={18} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
};

export const HomePage = () => {
  const { setHoveredPane, heroColor, setHeroColor } = useHeroContext();
  const [hoveredPane, setLocalHoveredPane] = useState(null);

  const handleSetHoveredPane = (pane) => {
    setLocalHoveredPane(pane);
    setHoveredPane(pane);
  };
  const [mobilePane, setMobilePane] = useState(1);

  const heroImage = HERO_IMAGES.heroGirl;

  const heroPanes = [
    {
      testId: "hero-pane-galleria",
      paneKey: "galleria",
      title: "Galleria Dental",
      smalltitle: "Spa",
      smalltitlecolor: "emeraled-green",

      subtitle: "Dentistry, practiced as fine art.",
      location: "New Jersey",
      link: "/locations/galleria",
      ctaButtons: [
        { label: "Restorative Dentistry", href: "/restorative-dentistry" },
        { label: "Book a Consult", href: "/contact" },
        { label: "732-462-5511", href: "tel:7324625511" },
      ],
    },
    {
      testId: "hero-pane-center",
      paneKey: "center",
      title: "Smiles by Dr. K",
      subtitle: "Two Locations.One Philosophy.Personalized Dentistry by Dr.K",
      link: "/gallery",
      ctaButtons: [
        { label: "Dentistry & Aesthetics", href: "/services" },
        { label: "Virtual Consult", href: "/contact" },
        { label: "What Patients Say", href: "/reviews" },
      ],
    },
    {
      testId: "hero-pane-essential",
      paneKey: "essential",
      title: "Essential Dental",
      smalltitle: "Spa",
      smalltitlecolor: "blue",
      subtitle: "Cosmetic Dentistry. Complete Care.",
      location: "New York",
      link: "/locations/essential",
      ctaButtons: [
        { label: "Smile make over", href: "/smilemakeover" },
        { label: "Book a Consult", href: "/contact" },
        { label: "516-621-2430", href: "tel:5166212430" },
      ],
    },
  ];

  // Auto-swipe mobile carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setMobilePane((prev) => (prev + 1) % heroPanes.length);
    }, 5000); // Auto-swipe every 5 seconds
    return () => clearInterval(interval);
  }, [heroPanes.length]);

  // Swipe detection for mobile carousel
  useEffect(() => {
    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe();
    };

    const handleSwipe = () => {
      const diff = touchStartX - touchEndX;
      const threshold = 50; // minimum distance for swipe

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          // Swiped left - go to next pane
          setMobilePane((prev) => (prev + 1) % heroPanes.length);
        } else {
          // Swiped right - go to previous pane
          setMobilePane(
            (prev) => (prev - 1 + heroPanes.length) % heroPanes.length,
          );
        }
      }
    };

    const heroSection = document.querySelector('[data-testid="hero-section"]');
    if (heroSection) {
      heroSection.addEventListener("touchstart", handleTouchStart);
      heroSection.addEventListener("touchend", handleTouchEnd);

      return () => {
        heroSection.removeEventListener("touchstart", handleTouchStart);
        heroSection.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [heroPanes.length]);

  return (
    <div data-testid="home-page">
      <Seo
        title={SEO.home.title}
        description={SEO.home.description}
        keywords={SEO.home.keywords}
        schemas={[
          organizationSchema(),
          websiteSchema(),
          ...LOCATIONS.map((l) => dentistSchema(l)),
          aggregateRatingSchema(),
        ]}
      />
      {/* 3-Pane Hero — single common background image with hover-reveal panes */}
      <section
        className="relative h-screen lg:h-auto"
        style={{ backgroundColor: heroColor }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 overflow-hidden w-full h-full">
          <img
            src={heroImage}
            alt="Smiles by Dr. K"
            className="absolute inset-0 w-full h-full lg:object-contain object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/20 via-ink/15 to-ink/55" />
          {/* <div className="absolute inset-0 bg-gradient-to-r from-ink/30 via-transparent to-ink/30" /> */}
        </div>

        {/* Desktop Grid View */}
        <div className="relative hidden lg:grid grid-cols-3">
          {heroPanes.map((pane, idx) => (
            <HeroPane
              key={idx}
              testId={pane.testId}
              paneKey={pane.paneKey}
              side={idx === 1 ? "center" : "side"}
              hoveredPane={hoveredPane}
              setHoveredPane={handleSetHoveredPane}
              title={pane.title}
              smallTitle={pane.smalltitle}
              smallTitleColor={pane.smalltitlecolor}
              subtitle={pane.subtitle}
              location={pane.location}
              link={pane.link}
              ctaButtons={pane.ctaButtons}
              heroColor={heroColor}
              setHeroColor={setHeroColor}
            />
          ))}
        </div>

        {/* Mobile Carousel View */}
        <div className="relative lg:hidden h-screen md:min-h-[680px] overflow-hidden">
          <div
            className="flex h-full transition-transform duration-500"
            style={{ transform: `translateX(-${mobilePane * 100}%)` }}
          >
            {heroPanes.map((pane, idx) => (
              <div
                key={idx}
                className="w-full h-full flex-shrink-0 flex flex-col items-center justify-start px-6 py-12 pt-20 text-center"
              >
                <h1 className="font-script text-cream text-5xl sm:text-6xl md:text-7xl leading-none mt-4 drop-shadow-[0_4px_22px_rgba(0,0,0,0.55)]">
                  {pane.title}
                  {pane.smalltitle && (
                    <span
                      className={` font-script  text-lg sm:text-xl md:text-2xl tracking-wide ${
                        {
                          "emeraled-green": "text-[#047857]",
                          "emerald-green": "text-[#047857]",
                          blue: "text-[#00FFFF]",
                          gold: "text-gold-light",
                          cream: "text-cream",
                        }[pane.smalltitlecolor] || "text-gold-light"
                      }`}
                    >
                      {pane.smalltitle}
                    </span>
                  )}
                </h1>
                <div className="mt-6 flex flex-col items-center">
                  <div className="max-w-md">
                    <p className="font-serif italic text-cream/95 text-lg sm:text-xl md:text-2xl leading-snug drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
                      {pane.subtitle}
                    </p>
                  </div>
                  <div className="mt-6 flex flex-row gap-4 flex-wrap justify-center ">
                    {pane.ctaButtons.map((btn, bidx) => (
                      <Link
                        key={bidx}
                        to={btn.href}
                        className="text-cream text-[9px] sm:text-[10px] tracking-[0.3em] uppercase border-t border-gold pt-1 hover:text-gold-light transition-colors max-w-[100px]"
                      >
                        {btn.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Carousel Navigation - Below Header */}
          <div
            className="absolute lg:hidden left-0 right-0 flex justify-center gap-2 py-3"
            style={{ top: "70px" }}
          >
            {heroPanes.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setMobilePane(idx)}
                className={`h-2 transition-all duration-300 ${idx === mobilePane ? "w-8 bg-gold" : "w-2 bg-cream/50"}`}
                aria-label={`Go to pane ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Smile gallery */}
      <section className="py-24 container-x border-b border-gold">
        <div className="flex items-end justify-between mb-10">
          <div>
            {/* <div className="label-eyebrow mb-4">Smile Gallery</div> */}
            <h3 className="font-script text-3xl md:text-4xl font-light">
              Smiles by Dr.K.
            </h3>
          </div>

          <div className="lg:col-span-5 lg:text-right reveal">
            <Link
              to="/gallery"
              data-testid="home-all-services-link"
              className="btn-outline"
            >
              Smiles Gallery
            </Link>
          </div>
        </div>
        <SmileGalleryGrid
          fromApi
          limit={6}
          order="asc"
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 md:gap-3"
        />
      </section>

      {/* Meet Dr. K Preview */}
      <section className="py-10 bg-ink text-bone">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6 reveal">
            <div className="scale-150 overflow-hidden">
              <img
                src={IMAGES.Drk}
                alt="Dr. Natalie Krasnyansky"
                className="w-full h-full object-contain object-middle"
              />
            </div>
          </div>
          <div className="lg:col-span-6 reveal">
            {/* <div className="label-eyebrow mb-6">Welcome to</div> */}
            <h2 className="heading-display text-bone text-4xl md:text-5xl lg:text-6xl">
              {" "}
              <span className="italic text-gold">Dr.K's Philosophy.</span>
            </h2>
            <p className="mt-8 text-white/75 leading-relaxed max-w-xl text-lg">
              {/* {BRAND.welcome} */}
              <span className="block text-[#fbf2c4]">
                Dentistry is one of the few professions where science, artistry,
                and compassion come together to improve lives.
              </span>
            </p>
            <p className="mt-4 text-white/75 leading-relaxed max-w-xl">
              That belief has guided me since the day I chose this profession
              and continues to shape every decision I make today. I believe
              exceptional care begins by listening. Every patient deserves
              honesty, respect, and treatment that is thoughtfully designed
              around their individual needs rather than a standardized approach.
            </p>
            <p className="mt-4 text-white/75 leading-relaxed max-w-xl">
              My goal has never been simply to restore teeth. It is to create
              healthy, functional, naturally beautiful smiles that enhance
              confidence while preserving long-term oral health.
            </p>
            <p className="mt-4 text-white/75 leading-relaxed max-w-xl">
              Every smile represents a relationship built on trust, and that
              trust is the greatest responsibility I have as a dentist.
            </p>
            <Link
              to="/about"
              data-testid="home-meet-drk-link"
              className="btn-outline-light mt-10"
            >
              Meet Dr. K
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className=" py-10  bg-ink/30">
        <div className=" container-x ">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-end mb-16">
            <div className="lg:col-span-7">
              {/* <SectionHeading
                eyebrow=""
                title="Dentistry."
                italicWord=""
                kicker="Every smile we design begins with a conversation, not a chair. Below — a small selection of the work that defines us."
              /> */}
              <div className="lg:col-span-12">
                <h3 className="heading-display text-5xl">
                  Personalized{" "}
                  <span className="italic text-[#016e0e]">Dentistry</span> by
                  Dr.K
                </h3>
                <p className="mt-4 text-xl max-w-2xl">
                  Every smile we design begins with a conversation, not a chair.
                  Below — a small selection of the work that defines us.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5 lg:text-right reveal">
              <Link
                to="/services"
                data-testid="home-all-services-link"
                className="btn-outline"
              >
                All Services
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-px bg-ink/10 sm:gap-2 md:gap-3">
            {SERVICES.slice(0, 9).map((s, i) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className={`group relative bg-bone p-8 md:p-10 aspect-[4/5] flex flex-col justify-between overflow-hidden reveal ${
                  i === 10 ? "hidden md:flex lg:hidden" : ""
                }`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div
                  className="absolute  inset-0 bg-cover bg-center opacity-50 group-hover:opacity-100 transition-opacity duration-700"
                  style={{
                    backgroundImage: `url(${Array.isArray(s.image) ? s.image[0] : s.image})`,
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/45 via-emerald-900/10 to-transparent pointer-events-none" />
                <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/30 transition-colors duration-700" />
                <div className="relative">
                  <div className="label-eyebrow text-gold-dark group-hover:text-gold-light transition-colors">
                    {String(i + 1).padStart(2, "0")} — {s.category}
                  </div>
                </div>
                <div className="relative">
                  <h3 className="font-serif text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light text-ink group-hover:text-white transition-colors leading-tight">
                    {s.name}
                  </h3>
                  <p className="mt-3 text-xs sm:text-sm text-ink/60 group-hover:text-white/70 transition-colors max-w-xs">
                    {s.tagline}
                  </p>
                  <div className="mt-6 flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-ink group-hover:text-gold transition-colors">
                    Discover{" "}
                    <ArrowUpRight
                      size={14}
                      strokeWidth={1.4}
                      className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <SpaServicesSection services={SPA_SERVICES} />

      {/* Booking CTA Banner */}
      <section className="relative py-10  overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMAGES.clinicInterior3}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-ink/70" />
        </div>
        <div className="relative container-x text-center text-white">
          <h2 className="heading-display text-3xl md:text-4xl lg:text-5xl max-w-3xl mx-auto">
            Get Expert Guidance{" "}
            <span className="italic text-gold-light">Before You Commit.</span>
          </h2>
          <p className="mt-8 text-white/75 leading-relaxed max-w-2xl mx-auto text-md">
            Whether you're considering a smile makeover, cosmetic dentistry,
            dental implants, full-mouth rehabilitation, or simply want a trusted
            second opinion, Dr. K can review your photos, X-rays, scans, and
            treatment recommendations to help you better understand your options
            before you proceed.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/contact"
              data-testid="cta-book-visit"
              className="btn-outline-light"
            >
              Book a Virtual Consult
            </Link>
            {/* <div className="flex flex-col sm:flex-row gap-6 mt-4 sm:mt-0 sm:ml-6">
              {LOCATIONS.map((l) => (
                <a
                  key={l.slug}
                  href={`tel:${l.phone.replace(/\D/g, "")}`}
                  className="flex items-center gap-2 text-white/80 hover:text-gold transition-colors text-sm tracking-wide"
                >
                  <Phone size={14} strokeWidth={1.2} /> {l.city.split(",")[0]} ·{" "}
                  {l.phone}
                </a>
              ))}
            </div> */}
          </div>
        </div>
      </section>
      {/* Testimonials carousel */}
      <section className="py-20 bg-ink border-t border-ink/5">
        <div className="container-x">
          <SectionHeading
            eyebrow="What we cherishest the most is"
            title="What Patients Say"
            align="center"
            light
          />
          <div className="container-x">
            <VideoTestimonialsSection />
          </div>
          <TestimonialsCarouselEmbla />
        </div>
      </section>

      {/* Two Locations */}
      {/* <section className="py-10 container-x">
        <div className="lg:col-span-12">
          
          <h3 className="heading-display text-3xl max-w-3xl mx-auto">
            <span className="italic text-[#00ff1a]">Two Locations.</span> One Philosophy
           
            Personalized Dentistry{" "}
            <span className="italic text-gold-light">by Dr.K</span>
          </h3>
            <p className="mt-4 text-center text-lg max-w-3xl mx-auto">
              Every smile we design begins with a conversation, not a chair. Below — a small selection of the work that defines us.
            </p>

        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {LOCATIONS.map((loc) => (
            <Link
              to={`/locations/${loc.slug}`}
              key={loc.slug}
              data-testid={`location-card-${loc.slug}`}
              className="group relative aspect-[5/6] overflow-hidden reveal"
            >
              <img
                src={loc.image}
                alt={loc.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/30 to-black/80" />
              <div className="relative h-full flex flex-col justify-end p-8 md:p-12 text-white">
                <div className="text-[10px] tracking-[0.4em] uppercase text-gold-light">
                  {loc.city}
                </div>
                <h3 className="font-serif text-4xl md:text-5xl font-light mt-3">
                  {loc.name}
                </h3>
                <p className="mt-3 text-white/70 max-w-md">{loc.focus}</p>
                <div className="mt-6 flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase border-b border-gold inline-block w-fit pb-1 group-hover:text-gold transition-colors">
                  Visit <ArrowUpRight size={14} strokeWidth={1.4} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section> */}

      {/* Instagram strip */}
      {/* <section className="py-24 container-x">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-eyebrow mb-4">{BRAND.instagram}</div>
            <h3 className="font-serif text-3xl md:text-4xl font-light">From the studio.</h3>
          </div>
          <a href="https://instagram.com" data-testid="home-instagram-link" className="hidden md:flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase link-underline">
            <Instagram size={14} strokeWidth={1.4} /> Follow
          </a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-3">
          {[IMAGES.smileCenter, IMAGES.clinicInterior1, IMAGES.smileOutdoor, IMAGES.clinicInterior2, IMAGES.technology, IMAGES.clinicInterior3].map((img, i) => (
            <div key={i} className="aspect-square overflow-hidden group cursor-pointer" data-testid={`instagram-tile-${i}`}>
              <img src={img} alt="" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
            </div>
          ))}
        </div>
      </section> */}
    </div>
  );
};
